import { TICK_MS } from "odyssey-shared";
import type { TravelEvent, TravelOffer } from "./wire.ts";
import { brokenLine, dimmedWords, refusedLine } from "./lines.ts";

/**
 * Waypoint travel on the page (ADR-137 decisions 22 and 23, P2.11-T38): the offer the world map shows in travel mode
 * and the line the player has picked in it, and the channel bar's state. An offer opens the world map at Region; the
 * map's closing discards the offer; a pick and Travel send `travel` with the offer's serial; the channel's start closes
 * the map and shows the bar for the ticks the server named, Escape cancelling it; its break shows the bar's interrupted
 * look a moment and says why on the message line; a refusal says why there and changes nothing else. Every rule is the
 * map server's: the page only words what it is told.
 */

/**
 * How long the bar shows its interrupted look after a break, in milliseconds.
 */
export const INTERRUPTED_MS = 1200;

/**
 * The channel bar's state: nothing, a channel to a place running from `startedMs` to `endsMs` on the page's clock, or
 * the interrupted look until `untilMs`.
 */
export type TravelBarState = { readonly kind: "none" } | { readonly kind: "channel"; readonly name: string; readonly startedMs: number; readonly endsMs: number } | { readonly kind: "interrupted"; readonly untilMs: number };

/**
 * What the world map and the bar read: the offer being shown (null when none), the index of the line picked (-1 for
 * none), the sentence of the last refusal while the offer is shown (empty for none), and the bar.
 */
export interface TravelSnapshot {
  readonly offer: TravelOffer | null;
  readonly picked: number;
  readonly refusal: string;
  readonly bar: TravelBarState;
}

/**
 * What the store asks of the page: the connection's travel requests and its view's tick, the world map's open state and
 * how to open it at Region on the character, the message line, the input router's layers (for Escape during a
 * channel), and the clock.
 */
export interface TravelPort {
  readonly sendTravelAsk: () => boolean;
  readonly sendTravel: (serial: number, index: number) => boolean;
  readonly sendTravelCancel: () => boolean;
  readonly tick: () => number;
  readonly openMap: () => void;
  readonly closeMap: () => void;
  readonly mapOpen: () => boolean;
  readonly onMapChange: (listener: () => void) => () => void;
  readonly say: (line: string) => void;
  readonly openLayer: (onClose: () => void) => { readonly close: () => void };
  readonly now: () => number;
}

/**
 * The page's travel state.
 */
export class TravelStore {
  private snapshot: TravelSnapshot = { offer: null, picked: -1, refusal: "", bar: { kind: "none" } };
  private readonly listeners = new Set<() => void>();
  private layer: { readonly close: () => void } | null = null;
  private clearTimer: ReturnType<typeof setTimeout> | null = null;

  /**
   * Makes the store and follows the world map's closing, which discards the offer.
   *
   * @param port What the store asks of the page.
   */
  public constructor(private readonly port: TravelPort) {
    port.onMapChange(() => {
      if (!port.mapOpen() && (this.snapshot.offer !== null || this.snapshot.refusal !== "")) {
        this.set({ ...this.snapshot, offer: null, picked: -1, refusal: "" });
      }
    });
  }

  /**
   * Listens for changes.
   *
   * @param listener Called after each change.
   * @returns A function that stops listening.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The current state, the same object until it changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): TravelSnapshot => this.snapshot;

  /**
   * The world map's Travel button: asks the map server for an offer, from the place the character stands in or from
   * anywhere, which it decides. A request the connection cannot send (not ready, or withheld while frozen) is said to
   * be refused, as the server would word it, so the press is never met with silence.
   */
  public readonly ask = (): void => {
    if (!this.port.sendTravelAsk()) {
      this.unsent();
    }
  };

  /**
   * Picks a line of the offer. A line that cannot be paid now is not picked.
   *
   * @param index The place's index in the offer.
   */
  public readonly pick = (index: number): void => {
    const place = this.snapshot.offer?.places[index];
    if (place === undefined || !place.affordable || index === this.snapshot.picked) {
      return;
    }
    this.set({ ...this.snapshot, picked: index, refusal: "" });
  };

  /**
   * Says why a line cannot be paid now, in the offer's footer, as a press on its mark. A line that can be paid says
   * nothing.
   *
   * @param index The place's index in the offer.
   */
  public readonly explain = (index: number): void => {
    const place = this.snapshot.offer?.places[index];
    if (place === undefined || place.affordable) {
      return;
    }
    this.set({ ...this.snapshot, refusal: dimmedWords(place) });
  };

  /**
   * Travels to the picked line, or to the line named: sends `travel` with the offer's serial. Nothing changes until the
   * server answers.
   *
   * @param index The place's index; the picked line's when omitted.
   */
  public readonly travel = (index: number = this.snapshot.picked): void => {
    const offer = this.snapshot.offer;
    const place = offer?.places[index];
    if (offer === null || place === undefined || !place.affordable) {
      return;
    }
    if (!this.port.sendTravel(offer.serial, index)) {
      this.unsent();
    }
  };

  /**
   * Says a refusal on the message line and, while the world map is open, in the map as well, since the window covers
   * the message line: in the offer's footer while one is shown, else in the note over the map's top left.
   *
   * @param line The refusal in words.
   */
  private refuse(line: string): void {
    this.port.say(line);
    if (this.snapshot.offer !== null || this.port.mapOpen()) {
      this.set({ ...this.snapshot, refusal: line });
    }
  }

  /**
   * Says a request went unsent: the general refusal on the message line, and with the offer while one is shown.
   */
  private unsent(): void {
    this.refuse(refusedLine("unknown"));
  }

  /**
   * Takes a travel frame from the connection.
   *
   * @param event The frame.
   */
  public receive(event: TravelEvent): void {
    if (event.kind === "offer") {
      const first = event.offer.places.findIndex((place) => place.affordable);
      this.set({ ...this.snapshot, offer: event.offer, picked: first, refusal: "" });
      this.port.openMap();
      return;
    }
    const state = event.state;
    if (state.state === "channel") {
      const now = this.port.now();
      const ends = now + Math.max(0, state.endsTick - this.port.tick()) * TICK_MS;
      this.set({ offer: null, picked: -1, refusal: "", bar: { kind: "channel", name: state.name, startedMs: now, endsMs: ends } });
      this.port.closeMap();
      this.openLayer();
      return;
    }
    if (state.state === "done") {
      this.closeLayer();
      this.set({ ...this.snapshot, bar: { kind: "none" } });
      return;
    }
    if (state.state === "refused") {
      this.refuse(refusedLine(state.reason));
      return;
    }
    this.closeLayer();
    this.port.say(brokenLine(state.reason));
    const until = this.port.now() + INTERRUPTED_MS;
    this.set({ ...this.snapshot, bar: { kind: "interrupted", untilMs: until } });
    this.clearLater(INTERRUPTED_MS);
  }

  /**
   * Ends the page's travel state at a disconnect or a change of map: the channel lives on the session (ADR-137
   * decision 15), so nothing of it outlives the connection.
   */
  public reset(): void {
    this.closeLayer();
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
      this.clearTimer = null;
    }
    this.set({ offer: null, picked: -1, refusal: "", bar: { kind: "none" } });
  }

  /**
   * Opens the channel's layer, whose closing by Escape sends `travelCancel`.
   */
  private openLayer(): void {
    this.closeLayer();
    const layer = this.port.openLayer(() => {
      if (this.layer !== layer) {
        return;
      }
      this.layer = null;
      this.port.sendTravelCancel();
    });
    this.layer = layer;
  }

  /**
   * Closes the channel's layer without cancelling anything.
   */
  private closeLayer(): void {
    const layer = this.layer;
    this.layer = null;
    layer?.close();
  }

  /**
   * Clears the bar's interrupted look after a while, unless something else has replaced it.
   *
   * @param ms The wait.
   */
  private clearLater(ms: number): void {
    if (this.clearTimer !== null) {
      clearTimeout(this.clearTimer);
    }
    this.clearTimer = setTimeout(() => {
      this.clearTimer = null;
      if (this.snapshot.bar.kind === "interrupted") {
        this.set({ ...this.snapshot, bar: { kind: "none" } });
      }
    }, ms);
  }

  /**
   * Replaces the snapshot and tells the listeners.
   *
   * @param next The new snapshot.
   */
  private set(next: TravelSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
